/**
 * Layout, kolizje i proceduralne tekstury wnętrza Weterynarza (świat 1600×720).
 * Zero emoji — same kształty Graphics → generateTexture (wzorzec park/playgroundVisuals).
 */
import Phaser from 'phaser';
import type { ColliderRect } from '../../core/world/index.js';
import {
  VET_CARE_ACTIONS,
  VET_DECO_TEXTURES,
  type VetCareId,
} from './vetLogic.js';

export const VET_WORLD_W = 1600;
export const VET_WORLD_H = 720;

const TEX_SIZE = 128;

export type VetDecorKind =
  | 'stethoscope'
  | 'bandage'
  | 'thermometer'
  | 'plant'
  | 'medicine';

/** Mapa texture key → rodzaj rysunku. */
export const VET_TEXTURE_SHAPES: Readonly<Record<string, VetDecorKind>> = {
  vet_tool_stethoscope: 'stethoscope',
  vet_tool_bandage: 'bandage',
  vet_tool_thermometer: 'thermometer',
  [VET_DECO_TEXTURES.plant]: 'plant',
  [VET_DECO_TEXTURES.medicine]: 'medicine',
};

/** Stół do badania. */
export const EXAM_TABLE = {
  x: 900,
  y: 320,
  w: 280,
  h: 90,
} as const;

/** Półka z lekami. */
export const MED_SHELF = {
  x: 1280,
  y: 220,
  w: 200,
  h: 160,
} as const;

/**
 * Start gracza — poza krzesłami poczekalni (stary 180,500 leżał w AABB krzesła).
 */
export const PLAYER_START = { x: 420, y: 600 } as const;

/** Krzesła poczekalni — klik = siadanie. */
export const VET_WAITING_CHAIRS = [
  { id: 'chair-a', x: 160, y: 500, approachX: 160, approachY: 560 },
  { id: 'chair-b', x: 260, y: 540, approachX: 260, approachY: 590 },
  { id: 'chair-c', x: 360, y: 500, approachX: 360, approachY: 560 },
] as const;

/** Recepcja / asystent — klikalne powitanie. */
export const RECEPTION_POS = { x: 380, y: 170 } as const;

/** Hotspoty czynności — pozycja w świecie + id opieki. */
export type VetCareSpot = {
  careId: VetCareId;
  x: number;
  y: number;
};

export const VET_CARE_SPOTS: readonly VetCareSpot[] = [
  { careId: 'stethoscope', x: 900, y: 280 },
  { careId: 'bandage', x: 1280, y: 300 },
  { careId: 'thermometer', x: 1080, y: 260 },
] as const;

/** Podejście do stołu (osłuchanie / temperatura). */
export const EXAM_APPROACH = { x: 900, y: 420 } as const;
export const SHELF_APPROACH = { x: 1200, y: 400 } as const;

export const VET_COLLIDERS: ColliderRect[] = [
  // stół badania
  {
    x: EXAM_TABLE.x - EXAM_TABLE.w / 2,
    y: EXAM_TABLE.y - EXAM_TABLE.h / 2,
    width: EXAM_TABLE.w,
    height: EXAM_TABLE.h,
  },
  // półka
  {
    x: MED_SHELF.x - MED_SHELF.w / 2,
    y: MED_SHELF.y - MED_SHELF.h / 2,
    width: MED_SHELF.w,
    height: MED_SHELF.h * 0.45,
  },
  // biurko recepcji
  { x: 200, y: 200, width: 220, height: 50 },
  // krzesła poczekalni (AABB) — spodki, nie blokują spawnu na y=600
  { x: 120, y: 488, width: 80, height: 36 },
  { x: 220, y: 528, width: 80, height: 36 },
  { x: 320, y: 488, width: 80, height: 36 },
];

/** Czy punkt jest wolny od colliderów (testy spawnu). */
export function isVetPointClear(
  x: number,
  y: number,
  radius = 22,
  colliders: readonly ColliderRect[] = VET_COLLIDERS,
): boolean {
  for (const c of colliders) {
    const nearestX = Math.max(c.x, Math.min(x, c.x + c.width));
    const nearestY = Math.max(c.y, Math.min(y, c.y + c.height));
    const dx = x - nearestX;
    const dy = y - nearestY;
    if (dx * dx + dy * dy < radius * radius) return false;
  }
  return true;
}

/**
 * Upewnij się, że wszystkie tekstury weterynarza istnieją.
 * PNG z Preload mają pierwszeństwo; brak → narysuj proceduralnie.
 */
export function ensureVetTextures(scene: Phaser.Scene): void {
  for (const a of VET_CARE_ACTIONS) {
    ensureVetTexture(scene, a.textureKey);
  }
  ensureVetTexture(scene, VET_DECO_TEXTURES.plant);
  ensureVetTexture(scene, VET_DECO_TEXTURES.medicine);
}

export function ensureVetTexture(
  scene: Phaser.Scene,
  textureKey: string,
): string {
  if (scene.textures.exists(textureKey)) return textureKey;
  const kind = VET_TEXTURE_SHAPES[textureKey];
  if (!kind) return textureKey;

  const g = scene.make.graphics({ x: 0, y: 0 });
  drawVetShape(g, kind, TEX_SIZE, TEX_SIZE);
  g.generateTexture(textureKey, TEX_SIZE, TEX_SIZE);
  g.destroy();
  return textureKey;
}

export function vetShapeForTexture(
  textureKey: string,
): VetDecorKind | null {
  return VET_TEXTURE_SHAPES[textureKey] ?? null;
}

export function drawVetShape(
  g: Phaser.GameObjects.Graphics,
  kind: VetDecorKind,
  w: number,
  h: number,
): void {
  g.clear();
  const cx = w / 2;
  const cy = h / 2;
  switch (kind) {
    case 'stethoscope':
      drawStethoscope(g, cx, cy);
      break;
    case 'bandage':
      drawBandage(g, cx, cy);
      break;
    case 'thermometer':
      drawThermometer(g, cx, cy);
      break;
    case 'plant':
      drawPlant(g, cx, cy);
      break;
    case 'medicine':
      drawMedicine(g, cx, cy);
      break;
  }
}

function drawStethoscope(
  g: Phaser.GameObjects.Graphics,
  cx: number,
  cy: number,
): void {
  // słuchawki
  g.lineStyle(6, 0x90a4ae, 1);
  g.beginPath();
  g.arc(cx - 18, cy - 28, 14, Math.PI, 0, false);
  g.strokePath();
  g.beginPath();
  g.arc(cx + 18, cy - 28, 14, Math.PI, 0, false);
  g.strokePath();
  // rurki
  g.lineStyle(5, 0x78909c, 1);
  g.lineBetween(cx - 18, cy - 14, cx - 8, cy + 10);
  g.lineBetween(cx + 18, cy - 14, cx + 8, cy + 10);
  g.lineBetween(cx - 8, cy + 10, cx, cy + 22);
  g.lineBetween(cx + 8, cy + 10, cx, cy + 22);
  // membrana
  g.fillStyle(0x42a5f5, 1);
  g.fillCircle(cx, cy + 36, 18);
  g.fillStyle(0xbbdefb, 1);
  g.fillCircle(cx, cy + 36, 10);
  g.lineStyle(3, 0xffffff, 0.9);
  g.strokeCircle(cx, cy + 36, 18);
}

function drawBandage(
  g: Phaser.GameObjects.Graphics,
  cx: number,
  cy: number,
): void {
  // rolka
  g.fillStyle(0xfff8e1, 1);
  g.fillRoundedRect(cx - 36, cy - 28, 72, 56, 12);
  g.fillStyle(0xffe0b2, 1);
  g.fillEllipse(cx, cy, 70, 52);
  g.fillStyle(0xffffff, 0.95);
  g.fillEllipse(cx, cy, 48, 36);
  // krzyżyk medyczny
  g.fillStyle(0xef5350, 1);
  g.fillRect(cx - 6, cy - 18, 12, 36);
  g.fillRect(cx - 18, cy - 6, 36, 12);
  g.lineStyle(3, 0xffffff, 0.85);
  g.strokeEllipse(cx, cy, 70, 52);
}

function drawThermometer(
  g: Phaser.GameObjects.Graphics,
  cx: number,
  cy: number,
): void {
  // trzon
  g.fillStyle(0xeceff1, 1);
  g.fillRoundedRect(cx - 10, cy - 48, 20, 72, 8);
  // skala
  g.fillStyle(0xef5350, 1);
  g.fillRoundedRect(cx - 4, cy - 30, 8, 48, 4);
  // bańka
  g.fillStyle(0xef5350, 1);
  g.fillCircle(cx, cy + 32, 16);
  g.fillStyle(0xffcdd2, 0.8);
  g.fillCircle(cx - 4, cy + 28, 6);
  g.lineStyle(3, 0xffffff, 0.9);
  g.strokeRoundedRect(cx - 10, cy - 48, 20, 72, 8);
  g.strokeCircle(cx, cy + 32, 16);
}

function drawPlant(
  g: Phaser.GameObjects.Graphics,
  cx: number,
  cy: number,
): void {
  // doniczka
  g.fillStyle(0xd7a86e, 1);
  g.fillRoundedRect(cx - 28, cy + 16, 56, 36, 6);
  g.fillStyle(0xc4956a, 1);
  g.fillRect(cx - 32, cy + 12, 64, 10);
  // liście
  g.fillStyle(0x66bb6a, 1);
  g.fillEllipse(cx - 16, cy - 8, 36, 48);
  g.fillEllipse(cx + 16, cy - 4, 34, 44);
  g.fillStyle(0x81c784, 1);
  g.fillEllipse(cx, cy - 28, 40, 50);
  g.fillStyle(0xa5d6a7, 0.7);
  g.fillEllipse(cx - 6, cy - 36, 16, 20);
}

function drawMedicine(
  g: Phaser.GameObjects.Graphics,
  cx: number,
  cy: number,
): void {
  // buteleczka
  g.fillStyle(0x80cbc4, 1);
  g.fillRoundedRect(cx - 22, cy - 20, 44, 56, 8);
  g.fillStyle(0x4db6ac, 1);
  g.fillRect(cx - 14, cy - 36, 28, 18);
  g.fillStyle(0xffffff, 0.55);
  g.fillRoundedRect(cx - 14, cy - 8, 28, 28, 4);
  // kapsułka obok
  g.fillStyle(0xef9a9a, 1);
  g.fillEllipse(cx + 28, cy + 20, 28, 16);
  g.fillStyle(0xffe082, 1);
  g.fillEllipse(cx + 38, cy + 20, 14, 16);
}
